import React, { useEffect, useState, useContext } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Grid } from 'react-bootstrap';
import { pickBy, has, isEqual } from 'lodash';
import qs from 'qs';

import FilterGroup from '../../components/Filters/FilterGroup';
import SearchGroup from '../../components/Search/SearchGroup';
import Pagination from '../../components/Pagination';
import Spinner from '../../components/Loaders/Spinner';
import Tabs from '../../components/Tabs';
import Table from './components/Table';
import ModalsManager from './components/Modals/Manager';
import Loader from '../../components/Loaders/Loader';

import { getNavbarCountersAction } from '../../redux/actions/header';
import {
  bulkStatus,
  changeStatus,
  getConfig,
  getDetails,
  getList,
  getListPagination,
  getModes,
  getServices,
  getView,
} from '../../services/endpoints/cancel';
import { WebsocketContext } from '../../utils/WebsocketProvider';

const Cancel = () => {
  const { eventData } = useContext(WebsocketContext);

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const queryString = qs.parse(location.search.slice(1));

  const [init, setInit] = useState(false);
  const [data, setData] = useState({});
  const [config, setConfig] = useState({});
  const [values, setValues] = useState([]);
  const [isLoading, setIsLoading] = useState({ page: true, pagination: true, modal: false, modalSubmit: false, table: false });
  const [openModal, setOpenModal] = useState('');
  const [modalData, setModalData] = useState({});

  const onHideModals = () => {
    setOpenModal('');
    setModalData({});
  };

  const fetchList = async () => {
    setIsLoading((prevState) => ({ ...prevState, table: true }));
    try {
      const response = await getList(queryString);
      const isUpdatePagination = !isEqual(data?.filters, response?.data?.filters)
      if (response.success) {
        setData((prevState) => ({
          ...response.data,
          pagination: isUpdatePagination ? response.data.pagination : {
            ...prevState.pagination,
            current_page: response.data.pagination.current_page,
          },
        }));
      }
      setIsLoading((prevState) => ({ ...prevState, table: false }));
      return {
        ...response.data,
        need_pagination: isUpdatePagination,
      };
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, table: false }));
    }
  };

  const fetchListPagination = async () => {
    setIsLoading((prevState) => ({ ...prevState, pagination: true }));

    try {
      const response = await getListPagination(queryString);
      if (response.success) {
        setData((prevState) => ({ ...prevState, pagination: response.data.pagination }));
      }
      setIsLoading((prevState) => ({ ...prevState, pagination: false }));
      return response.data;
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, pagination: false }));
      return error;
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await getConfig(queryString);
      if (response.success) {
        setConfig(response.data);
        return response.data;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getAllData = async () => {
    setIsLoading((prevState) => ({ ...prevState, page: true }));

    try {
      const configResponse = !init ? await fetchConfig() : config;
      if (configResponse?.access?.list) {
        const listResponse = await fetchList();
        setIsLoading((prevState) => ({ ...prevState, page: false }));
        if (listResponse?.need_pagination) {
          await fetchListPagination();
        } else {
          setIsLoading((prevState) => ({ ...prevState, pagination: false }));
        }
      } else {
        setIsLoading((prevState) => ({ ...prevState, page: false }));
      }
      setInit(true);
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, page: false }));
    }
  };

  const fetchTableFilters = async (name) => {
    let request = null;

    switch (name) {
    case 'modes':
      request = getModes;
      break;

    case 'services':
      request = getServices;
      break;

    default:
      break;
    }

    try {
      const response = await request(queryString);

      if (response?.success) {
        setConfig((prevState) => ({ ...prevState, [name]: response?.data?.[name] }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onSortTable = (head) => {
    if (head.active === 'desc') {
      delete queryString.page;
      queryString.sort = head.asc;
    }
    if (head.active === 'asc' || !head.active) {
      delete queryString.page;
      queryString.sort = head.desc;
    }
    return history.push(`${location.pathname}?${(location.search = qs.stringify(queryString))}`);
  };

  const onChangeStatus = async () => {
    setIsLoading((prevState) => ({ ...prevState, modalSubmit: true }));
    try {
      await changeStatus(modalData?.item?.id, { status: modalData.actionKey });
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
      onHideModals('');
      fetchList();
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
    }
  };

  const onBulkStatus = async () => {
    setIsLoading((prevState) => ({ ...prevState, modalSubmit: true }));
    try {
      await bulkStatus({ status: modalData.actionKey, tasks: values });
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
      onHideModals('');
      fetchList();
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
    }
  };

  const fetchView = async (id) => {
    setIsLoading((prevState) => ({ ...prevState, modal: true }));
    try {
      const response = await getView(id);
      setModalData((prevState) => ({ ...prevState, view: response?.data }));
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    }
  };

  const fetchDetails = async (id) => {
    setIsLoading((prevState) => ({ ...prevState, modal: true }));
    try {
      const response = await getDetails(id);
      setModalData((prevState) => ({ ...prevState, details: response?.data }));
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    }
  };

  const getModalDetails = (type, id) => {
    switch (type) {
    case 'view':
      fetchView(id);
      break;

    case 'details':
      fetchDetails(id);
      break;

    default:
      break;
    }
  };

  const onSubmitModal = async () => {
    switch (modalData?.actionType) {
    case 'single':
      if (modalData?.title === 'Change status') {
        onChangeStatus();
      }
      break;

    case 'mass':
      if (modalData?.title === 'Change status') {
        onBulkStatus();
      }
      break;

    default:
      break;
    }
  };

  const onChangeSelectService = (options) => {
    if (options?.length === 0 && !has(queryString, 'service')) return;
    const newOptions = options.filter((option) => option.id !== null).map((opt) => opt.id);
    const queryObj = {
      ...queryString,
      service: newOptions,
    };
    if (has(queryObj, 'page')) delete queryObj.page;
    history.push({ pathname: location.pathname, search: qs.stringify({ ...pickBy(queryObj) }) });
  };

  useEffect(() => {
    dispatch(getNavbarCountersAction({ code: 'cancel-list' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getAllData();
    setValues([]);
    document.title = 'Cancel';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    switch (eventData?.name) {
    case 'menu_counter_update':
      if (eventData?.options?.code === 'cancel-list') {
        getAllData();
      }
      break;

    default:
      break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData]);

  return (
    <Grid fluid>
      <ModalsManager
        open={openModal}
        isLoading={isLoading.modal}
        isLoadingSubmit={isLoading.modalSubmit}
        modalData={modalData}
        values={values}
        statuses={config?.statuses}
        onSubmit={onSubmitModal}
        onHide={onHideModals}
      />

      <FilterGroup>
        {config?.statuses && <Tabs tab="status" tabsData={config?.statuses} />}
        <SearchGroup
          autocomplete="on"
          sectionName="cancel"
          placeholder="Search tasks"
          withSelect
          options={config?.search_types}
          filters={data?.filters}
          disabled={isLoading.page}
        />
      </FilterGroup>

      {isLoading.page && !init ? (
        <Loader />
      ) : (
        <div className="loader-container" style={{ minHeight: '600px' }}>
          {config?.access?.list ? (
            <>
              <Table
                items={data?.tasks}
                config={config}
                sort={data?.sort}
                filters={data?.filters}
                values={values}
                isLoading={isLoading.table}
                setOpenModal={setOpenModal}
                setValues={setValues}
                setModalData={setModalData}
                getModalDetails={getModalDetails}
                fetchTableFilters={fetchTableFilters}
                onSortTable={onSortTable}
                onChangeSelectService={onChangeSelectService}
              />

              {isLoading.pagination || isLoading.table ? (
                <div className="pagination">
                  <Spinner />
                </div>
              ) : (
                <Pagination
                  pages={data.pagination?.pages}
                  currentPage={data.pagination?.current_page}
                  itemsCount={data.pagination?.total_count}
                  pageSize={data.pagination?.page_size}
                  pagesLimit={data.pagination?.pages < 10 ? data.pagination?.pages : 10}
                />
              )}
            </>
          ) : null}
        </div>
      )}
    </Grid>
  );
};

export default Cancel;
